/**
 * AI Chat Combined Hook
 *
 * Composes smaller hooks into the unified useAIChat interface.
 * This is the main entry point for components that need AI Chat functionality.
 *
 * Split into:
 * - ai-chat-api.ts      → Pure API functions
 * - ai-chat-store.ts    → Zustand store, types, constants
 * - ai-chat-queries.ts  → Individual React Query hooks
 * - ai-chat-streaming.ts → SSE streaming logic
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AI_CHAT_QUERY_KEYS, DEFAULT_HISTORY_LIMIT, useConversationStore } from './ai-chat-store';
import {
  useChatHistory,
  useChatHistoryList,
  useCreateConversation,
  useRenameConversation as useRenameConversationMutation,
  useDeleteConversation as useDeleteConversationMutation,
  useSendMessage,
  useClearChatHistory,
  useConfirmAction,
  useCancelAction,
} from './ai-chat-queries';
import { useAIChatStreaming } from './ai-chat-streaming';
import { listConversations } from './ai-chat-api';

// Re-export only the hooks that consumers import from this file.
// Non-hook exports (types, constants, store) live in their own files:
//   - ai-chat-store.ts  → useConversationStore, AI_CHAT_QUERY_KEYS, types
//   - ai-chat-queries.ts → individual React Query hooks
//   - ai-chat-api.ts     → pure API functions
export {
  useDeleteConversation,
  useRenameConversation,
  useConfirmAction,
  useCancelAction,
} from './ai-chat-queries';

// ─────────────────────────────────────────────────────────────────────────────
// Combined hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Combined hook for all AI Chat operations with streaming support.
 *
 * Composes:
 * - Conversation store (current conversation, switching)
 * - Chat history queries (messages, loading)
 * - Streaming send (SSE typing effect)
 * - Conversation management (create, rename, delete)
 * - Action confirmation (confirm/cancel pending actions)
 */
export function useAIChat() {
  const { currentConversationId, setCurrentConversationId } = useConversationStore();
  const queryClient = useQueryClient();

  // Data queries
  const { data: historyData, isLoading: isLoadingHistory, error: historyError } = useChatHistory(currentConversationId || undefined);
  const { data: conversationsData, isLoading: isLoadingConversations } = useChatHistoryList();

  // Mutations
  const createConversationMutation = useCreateConversation();
  const renameConversationMutation = useRenameConversationMutation();
  const deleteConversationMutation = useDeleteConversationMutation();
  const sendMessageMutation = useSendMessage();
  const clearHistoryMutation = useClearChatHistory();
  const confirmActionMutation = useConfirmAction();
  const cancelActionMutation = useCancelAction();

  // Streaming
  const streaming = useAIChatStreaming(currentConversationId, setCurrentConversationId, historyData);

  // Compute display messages: prefer streaming messages while active
  const messages = (streaming.isStreaming || (streaming.streamingMessages.length > 0 && (!historyData?.messages?.length)))
    ? streaming.streamingMessages
    : (historyData?.messages ?? []);

  const conversations = conversationsData?.conversations ?? [];

  // ─────────────────────────────────────────────────────────────────────────
  // Conversation management actions
  // ─────────────────────────────────────────────────────────────────────────

  const newConversation = useCallback(async () => {
    const result = await createConversationMutation.mutateAsync(undefined);
    setCurrentConversationId(result.id);
    return result;
  }, [createConversationMutation, setCurrentConversationId]);

  /**
   * Start a fresh chat WITHOUT creating a server-side conversation.
   * The conversation is created lazily by the backend when the first
   * message is sent (the done event carries the new conversationId).
   * Eager creation used to litter the sidebar with empty conversations.
   */
  const startNewChat = useCallback(() => {
    // Ein neuer Chat beginnt leer — keine Reste aus einem früheren Chat ohne ID.
    queryClient.removeQueries({ queryKey: AI_CHAT_QUERY_KEYS.history(undefined, DEFAULT_HISTORY_LIMIT), exact: true });
    setCurrentConversationId(null);
  }, [queryClient, setCurrentConversationId]);

  const selectConversation = useCallback((conversationId: string) => {
    setCurrentConversationId(conversationId);
  }, [setCurrentConversationId]);

  const renameCurrentConversation = useCallback(async (conversationId: string, title: string) => {
    await renameConversationMutation.mutateAsync({ conversationId, title });
  }, [renameConversationMutation]);

  const deleteCurrentConversation = useCallback(async () => {
    if (!currentConversationId) return;
    await deleteConversationMutation.mutateAsync(currentConversationId);

    const updatedConversations = await listConversations(20, 0);
    if (updatedConversations.conversations.length > 0) {
      setCurrentConversationId(updatedConversations.conversations[0].id);
    } else {
      setCurrentConversationId(null);
    }
  }, [currentConversationId, deleteConversationMutation, setCurrentConversationId]);

  const clearCurrentHistory = useCallback(async () => {
    if (currentConversationId) {
      await clearHistoryMutation.mutateAsync(currentConversationId);
    }
  }, [currentConversationId, clearHistoryMutation]);

  const restartConversation = useCallback(async () => {
    startNewChat();
  }, [startNewChat]);

  // ─────────────────────────────────────────────────────────────────────────
  // Return unified interface
  // ─────────────────────────────────────────────────────────────────────────

  return {
    // State
    messages,
    conversations,
    currentConversationId,
    isLoading: isLoadingHistory || isLoadingConversations,
    isSending: sendMessageMutation.isPending || streaming.isStreaming,
    isStreaming: streaming.isStreaming,
    streamingContent: streaming.streamingContent,
    latestSuggestions: streaming.latestSuggestions,
    liveThinking: streaming.liveThinking,
    error: historyError || sendMessageMutation.error,

    // Actions
    send: streaming.send,
    cancelStreaming: streaming.cancelStreaming,
    clearHistory: clearCurrentHistory,
    confirmAction: confirmActionMutation.mutateAsync,
    cancelAction: cancelActionMutation.mutateAsync,

    // Conversation management
    newConversation,
    startNewChat,
    selectConversation,
    renameConversation: renameCurrentConversation,
    deleteConversation: deleteCurrentConversation,
    restartConversation,

    // Loading states
    isClearingHistory: clearHistoryMutation.isPending,
    isConfirmingAction: confirmActionMutation.isPending,
    isCancellingAction: cancelActionMutation.isPending,
    isCreatingConversation: createConversationMutation.isPending,
    isRenamingConversation: renameConversationMutation.isPending,
    isDeletingConversation: deleteConversationMutation.isPending,

    // Low-level access
    addOptimisticMessage: streaming.addOptimisticMessage,
    removeOptimisticMessage: streaming.removeOptimisticMessage,
  };
}

export default useAIChat;
